/**
 * Embeddings client for retrieve-then-rerank template selection.
 *
 * Talks to the server-side proxy at `/api/ai/openai/embeddings`
 * (see `apps/server/routes/ai.js`), which forwards to OpenAI's
 * `/v1/embeddings` endpoint. We never embed user-PII content here;
 * inputs are template metadata + the user's chat query, both of
 * which already flow to the planner today.
 *
 * Failure mode: every helper here returns `null` (or `0`) on
 * transport / API errors so callers can fall back to lexical
 * scoring instead of blocking the chat. We intentionally do NOT
 * throw — embeddings are an optimization, not a requirement.
 */

import { buildApiUrl } from "../config/api";
import { withCredentials } from "../apiClient";
import { fetchWithRetry, readModelError } from "../utils/http";
import { createLogger } from "../utils/logger";
import { withLangfuseMetadataHeader } from "./langfuseTrace";

const log = createLogger("Embeddings");

/**
 * Cheap, fast, 1536-dim model. Plenty for short template-name /
 * description retrieval where we only need top-K, not absolute
 * relevance calibration. Bumping to `text-embedding-3-large`
 * (3072 dims) is a one-line swap if recall ever becomes the gap.
 */
export const EMBEDDING_MODEL = "text-embedding-3-small";

/**
 * Soft cap so a runaway catalog never blows past OpenAI's per-
 * request input limit. The entitled template catalog is ~250
 * rows today, so a single call comfortably fits — but if a tenant
 * ever ships thousands, we batch in chunks of this size.
 */
const MAX_INPUTS_PER_REQUEST = 256;
const LANGFUSE_HEADER_RETRY_STATUS_CODES = new Set([400, 403, 431]);

interface EmbedTextsOptions {
  signal?: AbortSignal;
  logTag?: string;
  langfuseContext?: object;
}

interface OpenAIEmbeddingResponse {
  data?: Array<{ embedding?: number[]; index?: number }>;
  error?: { message?: string };
}

/**
 * Embed an array of strings in a single (or chunked) call.
 *
 * Returns `null` if the request fails for any reason. Returns
 * an array aligned with `texts` on success.
 */
export async function embedTexts(
  texts: string[],
  options: EmbedTextsOptions = {},
): Promise<number[][] | null> {
  if (texts.length === 0) return [];

  const out: number[][] = new Array(texts.length);
  const logTag = options.logTag ?? "Embeddings";

  for (let start = 0; start < texts.length; start += MAX_INPUTS_PER_REQUEST) {
    const chunk = texts.slice(start, start + MAX_INPUTS_PER_REQUEST);

    let response: Response;
    try {
      const langfuseMetadata =
        options.langfuseContext && typeof options.langfuseContext === "object"
          ? {
              ...(options.langfuseContext as Record<string, unknown>),
              phase: "embedding",
              provider: "openai",
              model: EMBEDDING_MODEL,
              label: logTag,
              inputCount: chunk.length,
            }
          : undefined;
      const requestInit = withCredentials({
        method: "POST",
        body: JSON.stringify({
          model: EMBEDDING_MODEL,
          input: chunk,
        }),
        signal: options.signal,
      });

      response = await fetchWithRetry(
        buildApiUrl("/api/ai/openai/embeddings"),
        withLangfuseMetadataHeader(requestInit, langfuseMetadata),
        1,
        logTag
      );

      if (
        !response.ok &&
        Boolean(langfuseMetadata) &&
        LANGFUSE_HEADER_RETRY_STATUS_CODES.has(response.status)
      ) {
        log.warn(
          `[${logTag}] retrying embeddings request without Langfuse metadata header (status=${response.status})`,
        );
        response = await fetchWithRetry(
          buildApiUrl("/api/ai/openai/embeddings"),
          requestInit,
          1,
          `${logTag}-NoLangfuseHeader`,
        );
      }
    } catch (err) {
      log.warn(`[${logTag}] fetch failed`, err);
      return null;
    }

    if (!response.ok) {
      const errMsg = await readModelError(response, logTag);
      log.warn(`[${logTag}] non-2xx response`, errMsg);
      return null;
    }

    let json: OpenAIEmbeddingResponse;
    try {
      json = (await response.json()) as OpenAIEmbeddingResponse;
    } catch (err) {
      log.warn(`[${logTag}] JSON parse failed`, err);
      return null;
    }

    const data = json.data;
    if (!Array.isArray(data) || data.length !== chunk.length) {
      log.warn(`[${logTag}] data length mismatch`, {
        expected: chunk.length,
        received: data?.length,
      });
      return null;
    }

    for (let i = 0; i < data.length; i++) {
      const entry = data[i];
      const vec = entry?.embedding;
      if (!Array.isArray(vec) || vec.length === 0) {
        log.warn(`[${logTag}] empty embedding at index ${start + i}`);
        return null;
      }
      // OpenAI returns entries in the order requested, but `index`
      // is authoritative when present — defensively prefer it.
      const targetIdx =
        typeof entry?.index === "number" ? start + entry.index : start + i;
      out[targetIdx] = vec;
    }
  }

  return out;
}

/**
 * Cosine similarity in [-1, 1]. OpenAI embeddings are unit-
 * normalized so this is effectively a dot product, but we
 * compute the full form so callers can pass arbitrary vectors
 * (e.g. cached vectors from disk that may have drifted).
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length);
  if (len === 0) return 0;

  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < len; i++) {
    const av = a[i];
    const bv = b[i];
    dot += av * bv;
    normA += av * av;
    normB += bv * bv;
  }

  if (normA === 0 || normB === 0) return 0;
  return dot / (Math.sqrt(normA) * Math.sqrt(normB));
}
